import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "./auth/supabase";
import { useAuth } from "./auth/AuthContext";

/* ============================================================
   Login / signup — real Supabase auth.

   Supports:
     - Email + password sign-in
     - Email + password sign-up (Supabase sends verification email)
     - Google OAuth     (works as soon as Google provider is enabled in Supabase)
     - Apple OAuth      (works once Apple Sign-In is enabled in Supabase;
                         requires a paid Apple Developer account)
   ============================================================ */
export default function Login() {
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // After a successful login, send user back to the page they came from,
  // or to /history by default.
  const redirectTo = location.state?.from || "/history";

  const isSignup = mode === "signup";

  // Already signed in? Bounce straight to /history.
  React.useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setInfoMsg("");

    if (!email || !password) {
      setErrorMsg("Enter your email and password.");
      return;
    }

    if (isSignup) {
      if (password.length < 8) {
        setErrorMsg("Password must be at least 8 characters.");
        return;
      }
      if (password !== confirmPassword) {
        setErrorMsg("Passwords don\u2019t match. Please re-enter them.");
        return;
      }
    }

    setSubmitting(true);
    try {
      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { display_name: name || null } },
        });
        if (error) throw error;

        if (data.user && !data.session) {
          // Email-confirmation is on (the Supabase default).
          setInfoMsg(
            "Almost there — check your inbox for a confirmation email, then come back to sign in."
          );
        } else {
          // Email confirmation is off, user is signed in immediately.
          navigate(redirectTo, { replace: true });
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Auth error:", err);
      setErrorMsg(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOAuth = async () => {
    setErrorMsg("");
    setInfoMsg("");
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          // Bring them back to /login after the OAuth round-trip.
          // The AuthContext will then pick up the session and bounce them to /history.
          redirectTo: window.location.origin + "/login",
        },
      });
      if (error) throw error;
      // signInWithOAuth navigates the browser away to Google; no further code runs here.
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("OAuth error:", err);
      setErrorMsg(
        "Google Sign-In is not configured yet. Add Google as a provider in your Supabase dashboard."
      );
    }
  };

  const handleForgot = async () => {
    setErrorMsg("");
    setInfoMsg("");
    if (!email) {
      setErrorMsg("Enter your email above first, then click Forgot password.");
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (error) throw error;
      setInfoMsg("Password reset email sent. Check your inbox.");
    } catch (err) {
      setErrorMsg(err.message || "Could not send reset email.");
    }
  };

  return (
    <main>
      <section className="login-wrap">
        <aside className="login-aside">
          <div>
            <span className="mono-label">Create an account</span>
            <h2 className="big" style={{ marginTop: 14 }}>
              Save your checks. Pick up where you <em>left off.</em>
            </h2>
          </div>
          <ul>
            <li><span className="check">✓</span><span>Save every claim you&rsquo;ve checked to your private archive.</span></li>
            <li><span className="check">✓</span><span>Bookmark the verdicts you want to come back to.</span></li>
            <li><span className="check">✓</span><span>See your full history, sorted by date or verdict.</span></li>
            <li><span className="check">✓</span><span>Unlock the browser extension for one-click checks.</span></li>
          </ul>
          <div className="quote">
            &ldquo;I used to text screenshots to my dad. Now I send him a FactOrNot link and he actually opens it.&rdquo;
            <span className="attr">— Mara K., early user</span>
          </div>
        </aside>

        <div className="login-card">
          <div className="login-tabs" role="tablist">
            <button
              type="button"
              className={"login-tab" + (mode === "signin" ? " active" : "")}
              onClick={() => { setMode("signin"); setErrorMsg(""); setInfoMsg(""); }}
            >
              Sign in
            </button>
            <button
              type="button"
              className={"login-tab" + (mode === "signup" ? " active" : "")}
              onClick={() => { setMode("signup"); setErrorMsg(""); setInfoMsg(""); }}
            >
              Create account
            </button>
          </div>

          <h2>{isSignup ? "Create your account." : "Welcome back."}</h2>
          <p className="sub">
            {isSignup
              ? "It takes about 30 seconds. We only need an email."
              : "Sign in to save your fact-checks and see your history."}
          </p>

          {isSignup && (
            <div className="signup-notice" role="note">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
              <div>
                <strong>You&rsquo;ll need to confirm your email.</strong>{" "}
                After you click <em>Create account</em>, you&rsquo;ll receive a verification link from <strong>Supabase</strong> (our authentication provider) at the email you entered. Click the link to activate your account &mdash; check your spam folder if it doesn&rsquo;t arrive within a minute.
              </div>
            </div>
          )}

          {errorMsg && <div className="auth-msg error" role="alert">{errorMsg}</div>}
          {infoMsg && <div className="auth-msg info" role="status">{infoMsg}</div>}

          <form onSubmit={handleSubmit} noValidate>
            {isSignup && (
              <div className="field">
                <label htmlFor="name">Name (optional)</label>
                <div className="input-wrap">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <input id="name" type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
              </div>
            )}

            <div className="field">
              <label htmlFor="email">Email</label>
              <div className="input-wrap">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
                <input id="email" type="email" autoComplete="email" placeholder="you@somewhere.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            <div className="field">
              <label htmlFor="password">
                Password {isSignup && <span className="label-hint">at least 8 characters</span>}
              </label>
              <div className="input-wrap">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button type="button" className="toggle-pw" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </div>

            {isSignup && (
              <div className="field">
                <label htmlFor="confirm-password">Confirm password</label>
                <div
                  className={
                    "input-wrap" +
                    (confirmPassword && password !== confirmPassword ? " mismatch" : "")
                  }
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  <input
                    id="confirm-password"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  {confirmPassword && password === confirmPassword && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2f8a3f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-label="Passwords match">
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  )}
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="field-error">Passwords don&rsquo;t match yet.</p>
                )}
              </div>
            )}

            {isSignup ? (
              <div className="row">
                <label><input type="checkbox" /> Email me when a saved check gets updated sources</label>
              </div>
            ) : (
              <div className="row">
                <label><input type="checkbox" /> Keep me signed in</label>
                <button type="button" className="link-btn" onClick={handleForgot}>Forgot password?</button>
              </div>
            )}

            <button className="submit" type="submit" disabled={submitting}>
              {submitting
                ? "Working…"
                : isSignup
                ? "Create account →"
                : "Sign in →"}
            </button>

            <div className="divider">or continue with</div>

            <div className="sso sso-single">
              <button type="button" onClick={handleOAuth}>
                <svg width="16" height="16" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.7c-.2 1.4-1 2.5-2.1 3.3v2.7h3.4c2-1.8 3-4.5 3-7.9z" />
                  <path fill="#34A853" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.4-2.7c-.9.6-2.1 1-3.3 1-2.6 0-4.7-1.7-5.5-4H3v2.6C4.7 19.7 8.1 22 12 22z" />
                  <path fill="#FBBC05" d="M6.5 13.9c-.2-.6-.3-1.2-.3-1.9s.1-1.3.3-1.9V7.5H3a10 10 0 0 0 0 9z" />
                  <path fill="#EA4335" d="M12 6.2c1.5 0 2.8.5 3.8 1.5l2.9-2.9C16.9 3.2 14.7 2.3 12 2.3 8.1 2.3 4.7 4.6 3 7.5l3.5 2.6c.8-2.3 2.9-4 5.5-4z" />
                </svg>
                Continue with Google
              </button>
            </div>

            <p className="fineprint">
              By continuing you agree to our <Link to="/about">Terms</Link> and{" "}
              <Link to="/about">Privacy Policy</Link>. We don&rsquo;t sell your data. We don&rsquo;t want to.
            </p>
          </form>
        </div>
      </section>

      <footer>
        <div className="foot-bottom" style={{ borderTop: 0, paddingTop: 0 }}>
          <span>&copy; 2026 FactOrNot</span>
          <span>Privacy &middot; Terms &middot; Press</span>
        </div>
      </footer>
    </main>
  );
}